/**
 * Turns the agent's SSE thought-steps into a phase, and a phase into something
 * honest to say while the user waits.
 *
 * The rule that keeps this from becoming decoration: the PHASE always comes
 * from a real event, and the rotating messages within a phase only ever
 * describe work that phase is actually doing. We never cycle "Searching the
 * web" while the agent is writing prose.
 */

export type AgentPhase =
  | 'starting'
  | 'analyzing'
  | 'searching'
  | 'recalling'
  | 'reading'
  | 'writing'
  | 'recovering';

export interface ThoughtStepLike {
  type?: string;
  content?: string;
  tool?: string;
}

/**
 * Messages cycled while a phase lasts.
 *
 * The first entry is the plain truth and shows immediately; later entries only
 * appear if the phase runs long enough to need them, so a fast run never shows
 * anything speculative.
 */
export const PHASE_MESSAGES: Record<AgentPhase, string[]> = {
  starting: [
    'Getting started',
    'Reading your question',
    'Planning the analysis',
  ],
  analyzing: [
    'Analyzing your data',
    'Filtering your territory',
    'Crunching the numbers',
    'Grouping the results',
  ],
  searching: [
    'Searching the web',
    'Reading sources',
  ],
  recalling: [
    'Checking your playbook',
  ],
  reading: [
    'Reading the results',
    'Making sense of the numbers',
  ],
  writing: [
    'Writing your answer',
    'Putting it together',
  ],
  recovering: [
    'That approach did not work, trying another',
  ],
};

/** How long each message shows before the next one in the phase. */
export const MESSAGE_ROTATE_MS = 2600;

/**
 * Minimum time a phase stays on screen before a new one may replace it.
 *
 * Steps arrive in bursts -- an analyze_data start and its completion can be a
 * second apart -- and a message that flashes past faster than it can be read is
 * worse than not showing it at all.
 */
export const MIN_PHASE_MS = 2000;

const TOOL_PHASES: Record<string, AgentPhase> = {
  analyze_data: 'analyzing',
  search_web: 'searching',
  web_search: 'searching',
  get_session_variable: 'recalling',
};

/**
 * The phase implied by the steps so far.
 *
 * Only the most recent step matters -- earlier ones are history, shown
 * separately once the run finishes.
 */
export function derivePhase(steps: ThoughtStepLike[] | undefined): AgentPhase {
  if (!steps || steps.length === 0) return 'starting';

  const last = steps[steps.length - 1];
  const type = last?.type;

  if (type === 'error_step' || type === 'retry_step') return 'recovering';
  if (type === 'crafting_response') return 'writing';
  if (type === 'tool_complete') return 'reading';
  if (type === 'tool_start') {
    return TOOL_PHASES[last?.tool || ''] || 'analyzing';
  }
  return 'starting';
}

/**
 * The message to show, given the phase and how long it has been running.
 *
 * Holds on the last message rather than looping back to the first: a phase that
 * outlives its vocabulary is a phase that is taking a while, and restarting the
 * cycle would suggest fresh progress that is not happening.
 */
export function phaseMessage(phase: AgentPhase, tick: number): string {
  const messages = PHASE_MESSAGES[phase];
  return messages[Math.min(tick, messages.length - 1)];
}
